import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { employeesAPI, officesAPI, certificationsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Pagination from '../components/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';
import {
  Search,
  User,
  Mail,
  Phone,
  Shield,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
  UserPlus,
  MapPin,
  Download,
  Upload,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  Plus as PlusIcon,
  UserCheck,
  UserX,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const workStatusColors = {
  available: 'bg-emerald-500',
  busy: 'bg-yellow-500',
  on_break: 'bg-blue-500',
  off_duty: 'bg-gray-500',
};

const workStatusLabels = {
  available: 'Available',
  busy: 'Busy',
  on_break: 'On Break',
  off_duty: 'Off Duty',
};

export default function Employees() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [offices, setOffices] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(null); // null = all, true = active, false = inactive

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Multi-selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showBulkStatusDialog, setShowBulkStatusDialog] = useState(null); // 'activate' or 'deactivate'
  const [showSmsDisclosure, setShowSmsDisclosure] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'employee',  // Keep for backwards compatibility
    roles: ['employee'],  // Multiple roles support
    office_location_id: '',
    certification_ids: [],
    cost_centers: [],  // Changed to array for multiple cost centers
  });
  const [importLoading, setImportLoading] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const fileInputRef = React.useRef(null);

  // Available roles
  const availableRoles = [
    { value: 'employee', label: 'Employee' },
    { value: 'contractor', label: 'Contractor' },
    { value: 'admin', label: 'Admin' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'manager', label: 'Manager' },
  ];

  const isAdmin = user?.role === 'admin' || user?.roles?.includes('admin');
  const totalPages = Math.ceil(totalItems / pageSize);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      if (showActiveOnly !== null) {
        params.active_only = showActiveOnly;
      }
      const response = await employeesAPI.getPaginated(params);
      const data = response.data;
      
      // Handle both paginated and array responses
      if (Array.isArray(data)) {
        setEmployees(data);
        setTotalItems(data.length);
      } else {
        setEmployees(data.items || []);
        setTotalItems(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, sortBy, sortOrder, showActiveOnly]);

  const fetchOtherData = useCallback(async () => {
    try {
      const [offRes, certRes] = await Promise.all([
        officesAPI.getAll(),
        certificationsAPI.getAll(),
      ]);
      setOffices(offRes.data);
      setCertifications(certRes.data);
    } catch (error) {
      console.error('Failed to fetch offices/certifications:', error);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
    fetchOtherData();
  }, [fetchEmployees, fetchOtherData]);

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage((p) => (p !== 1 ? 1 : p));
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleOpenForm = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        name: employee.name || '',
        email: employee.email || '',
        password: '', // Don't show existing password
        phone: employee.phone || '',
        role: employee.role || 'employee',
        office_location_id: employee.office_location_id || '',
        certification_ids: employee.certification_ids || [],
        cost_centers: employee.cost_centers || (employee.cost_center ? [employee.cost_center] : []),
        roles: employee.roles || (employee.role ? [employee.role] : ['employee']),
      });
    } else {
      setEditingEmployee(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        role: 'employee',
        roles: ['employee'],
        office_location_id: '',
        certification_ids: [],
        cost_centers: [],
      });
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingEmployee(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      role: 'employee',
      roles: ['employee'],
      office_location_id: '',
      certification_ids: [],
      cost_centers: [],
    });
  };

  const handleCertificationToggle = (certId) => {
    setFormData(prev => {
      const current = prev.certification_ids || [];
      if (current.includes(certId)) {
        return { ...prev, certification_ids: current.filter(id => id !== certId) };
      } else {
        return { ...prev, certification_ids: [...current, certId] };
      }
    });
  };

  // Handle role toggle for multi-role selection
  const handleRoleToggle = (roleValue) => {
    setFormData(prev => {
      const currentRoles = prev.roles || [];
      let newRoles;
      if (currentRoles.includes(roleValue)) {
        // Remove role (but keep at least one)
        newRoles = currentRoles.filter(r => r !== roleValue);
        if (newRoles.length === 0) newRoles = ['employee'];
      } else {
        newRoles = [...currentRoles, roleValue];
      }
      return { 
        ...prev, 
        roles: newRoles,
        role: newRoles[0]  // Keep primary role in sync
      };
    });
  };

  // Handle adding/removing cost centers
  const handleAddCostCenter = (costCenter) => {
    if (!costCenter.trim()) return;
    setFormData(prev => {
      const current = prev.cost_centers || [];
      if (!current.includes(costCenter.trim())) {
        return { ...prev, cost_centers: [...current, costCenter.trim()] };
      }
      return prev;
    });
  };

  const handleRemoveCostCenter = (costCenter) => {
    setFormData(prev => ({
      ...prev,
      cost_centers: (prev.cost_centers || []).filter(cc => cc !== costCenter)
    }));
  };

  const handleExportCSV = async () => {
    try {
      await employeesAPI.exportCSV();
      toast.success('Employees exported successfully');
    } catch (error) {
      toast.error('Failed to export employees');
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    try {
      const text = await file.text();
      const response = await employeesAPI.importCSV(text);
      const result = response.data;
      
      if (result.errors?.length > 0) {
        toast.warning(`Imported ${result.imported} employees. ${result.errors.length} errors occurred.`);
        console.log('Import errors:', result.errors);
      } else {
        toast.success(result.message);
      }
      fetchEmployees();
      setShowImportDialog(false);
    } catch (error) {
      console.error('Import error:', error.response?.data);
      // Handle Pydantic validation errors (array of objects) or string errors
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to import employees';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation errors
        errorMessage = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      
      toast.error(errorMessage);
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    if (!formData.cost_centers || formData.cost_centers.length === 0) {
      toast.error('At least one Division is required');
      return;
    }

    if (!editingEmployee && (!formData.password || formData.password.length < 6)) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      const payload = {
          ...formData,
          office_location_id: formData.office_location_id || null // Ensure null if empty string
      };
      
      if (editingEmployee) {
        await employeesAPI.update(editingEmployee.id, payload);
        toast.success('Employee updated successfully');
      } else {
        await employeesAPI.create(payload);
        toast.success('Employee created successfully');
      }
      handleCloseForm();
      fetchEmployees(); // Refresh employees list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await employeesAPI.delete(deleteId);
      toast.success('Employee deleted successfully');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employee');
    } finally {
      setDeleteId(null);
    }
  };

  const handleToggleActive = async (employeeId, currentStatus) => {
    try {
      // If currentStatus is true or undefined, we want to deactivate (set to false)
      // If currentStatus is false, we want to activate (set to true)
      const newStatus = currentStatus === false ? true : false;
      await employeesAPI.setActive(employeeId, newStatus);
      const statusText = newStatus ? 'activated' : 'deactivated';
      toast.success(`Employee ${statusText} successfully`);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee status');
    }
  };

  // Multi-selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      // Select all employees except current user
      const selectableIds = filteredEmployees
        .filter(emp => emp.id !== user?.id)
        .map(emp => emp.id);
      setSelectedIds(selectableIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (employeeId, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, employeeId]);
    } else {
      setSelectedIds(prev => prev.filter(id => id !== employeeId));
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkActionLoading(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const id of selectedIds) {
        try {
          await employeesAPI.delete(id);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Failed to delete employee ${id}:`, error);
        }
      }
      
      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} employee${successCount > 1 ? 's' : ''}`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to delete ${errorCount} employee${errorCount > 1 ? 's' : ''}`);
      }
      
      setSelectedIds([]);
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to delete employees');
    } finally {
      setBulkActionLoading(false);
      setShowBulkDeleteDialog(false);
    }
  };

  // Bulk activate/deactivate handler
  const handleBulkStatusChange = async (activate) => {
    if (selectedIds.length === 0) return;
    setBulkActionLoading(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const id of selectedIds) {
        try {
          await employeesAPI.setActive(id, activate);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Failed to update employee ${id}:`, error);
        }
      }
      
      const action = activate ? 'activated' : 'deactivated';
      if (successCount > 0) {
        toast.success(`Successfully ${action} ${successCount} employee${successCount > 1 ? 's' : ''}`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to ${action.slice(0, -1)} ${errorCount} employee${errorCount > 1 ? 's' : ''}`);
      }
      
      setSelectedIds([]);
      fetchEmployees();
    } catch (error) {
      toast.error(`Failed to update employees`);
    } finally {
      setBulkActionLoading(false);
      setShowBulkStatusDialog(null);
    }
  };

  // Employees already filtered server-side
  const filteredEmployees = employees;

  // Selection computed values (must be after filteredEmployees is defined)
  const isAllSelected = filteredEmployees.length > 0 && 
    filteredEmployees.filter(emp => emp.id !== user?.id).every(emp => selectedIds.includes(emp.id));

  const isSomeSelected = selectedIds.length > 0;

  // Sort handler
  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if clicking same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field with default ascending order
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Sort indicator component
  const SortIndicator = ({ field }) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" />
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Employees</h1>
          <p className="text-muted-foreground mt-1">
            Manage your team members ({employees.length} total)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-employees-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          {isAdmin && (
            <>
              <Button variant="outline" onClick={() => setShowImportDialog(true)} data-testid="import-employees-btn">
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
              <Button onClick={() => handleOpenForm()} data-testid="add-employee-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Employee
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="employee-search"
              />
            </div>
            <Select
              value={showActiveOnly === null ? 'all' : showActiveOnly ? 'active' : 'inactive'}
              onValueChange={(value) => {
                setShowActiveOnly(value === 'all' ? null : value === 'active');
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                <SelectItem value="active">
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-green-500" />
                    Active Only
                  </div>
                </SelectItem>
                <SelectItem value="inactive">
                  <div className="flex items-center gap-2">
                    <UserX className="w-4 h-4 text-orange-500" />
                    Inactive Only
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {isAdmin && isSomeSelected && (
        <Card className="border-primary bg-primary/5">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                  data-testid="select-all-checkbox"
                />
                <span className="font-medium">
                  {selectedIds.length} employee{selectedIds.length > 1 ? 's' : ''} selected
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedIds([])}
                  className="text-muted-foreground"
                >
                  Clear selection
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkStatusDialog('activate')}
                  disabled={bulkActionLoading}
                  className="border-green-300 text-green-600 hover:bg-green-50"
                  data-testid="bulk-activate-btn"
                >
                  <UserCheck className="w-4 h-4 mr-2" />
                  Activate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkStatusDialog('deactivate')}
                  disabled={bulkActionLoading}
                  className="border-orange-300 text-orange-600 hover:bg-orange-50"
                  data-testid="bulk-deactivate-btn"
                >
                  <UserX className="w-4 h-4 mr-2" />
                  Deactivate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkDeleteDialog(true)}
                  disabled={bulkActionLoading}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Employees Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="p-12 text-center">
              <UserPlus className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No employees found</p>
              {isAdmin && (
                <Button className="mt-4" onClick={() => handleOpenForm()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add your first employee
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && (
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={isAllSelected}
                        onCheckedChange={handleSelectAll}
                        data-testid="table-select-all"
                      />
                    </TableHead>
                  )}
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('name')}
                    data-testid="sort-name"
                  >
                    <div className="flex items-center">
                      Employee
                      <SortIndicator field="name" />
                    </div>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">Contact</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('role')}
                    data-testid="sort-role"
                  >
                    <div className="flex items-center">
                      Role
                      <SortIndicator field="role" />
                    </div>
                  </TableHead>
                  <TableHead>Office</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('cost_center')}
                    data-testid="sort-cost-center"
                  >
                    <div className="flex items-center">
                      Division
                      <SortIndicator field="cost_center" />
                    </div>
                  </TableHead>
                  <TableHead>Certs</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('work_status')}
                    data-testid="sort-status"
                  >
                    <div className="flex items-center">
                      Status
                      <SortIndicator field="work_status" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="hidden lg:table-cell cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('created_at')}
                    data-testid="sort-joined"
                  >
                    <div className="flex items-center">
                      Joined
                      <SortIndicator field="created_at" />
                    </div>
                  </TableHead>
                  {isAdmin && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.map((employee) => (
                  <TableRow 
                    key={employee.id} 
                    className={`table-row-hover ${selectedIds.includes(employee.id) ? 'bg-primary/5' : ''}`} 
                    data-testid={`employee-row-${employee.id}`}
                  >
                    {isAdmin && (
                      <TableCell>
                        {employee.id !== user?.id ? (
                          <Checkbox 
                            checked={selectedIds.includes(employee.id)}
                            onCheckedChange={(checked) => handleSelectOne(employee.id, checked)}
                            data-testid={`select-employee-${employee.id}`}
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground" title="Cannot select yourself">-</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback className={`${employee.is_active === false ? 'bg-gray-400' : 'bg-primary'} text-primary-foreground`}>
                            {employee.name.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className={`font-medium ${employee.is_active === false ? 'text-muted-foreground' : ''}`}>
                              {employee.name}
                            </p>
                            {employee.is_active === false && (
                              <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                                Inactive
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground md:hidden">
                            {employee.email}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                          {employee.email}
                        </div>
                        {employee.phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                            {employee.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(employee.roles || [employee.role]).map((role) => (
                          <Badge 
                            key={role}
                            variant={role === 'admin' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            <Shield className="w-3 h-3 mr-1" />
                            {role}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {employee.office_location_name ? (
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                          {employee.office_location_name}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground italic">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {employee.cost_center ? (
                        <span className="text-sm">{employee.cost_center}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground italic">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {employee.certification_names && employee.certification_names.length > 0 ? (
                        <div title={employee.certification_names.join(', ')}>
                           <Badge variant="outline" className="text-xs cursor-help">
                             {employee.certification_names.length} Certs
                           </Badge>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground italic">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${workStatusColors[employee.work_status] || workStatusColors.off_duty}`} />
                        <span className="text-sm">
                          {workStatusLabels[employee.work_status] || 'Off Duty'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-sm text-muted-foreground">
                        {format(parseISO(employee.created_at), 'MMM d, yyyy')}
                      </span>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`employee-menu-${employee.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenForm(employee)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {employee.id !== user?.id && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleToggleActive(employee.id, employee.is_active)}
                                  data-testid={`toggle-active-${employee.id}`}
                                >
                                  {employee.is_active !== false ? (
                                    <>
                                      <UserX className="w-4 h-4 mr-2 text-orange-500" />
                                      <span className="text-orange-600">Deactivate</span>
                                    </>
                                  ) : (
                                    <>
                                      <UserCheck className="w-4 h-4 mr-2 text-green-500" />
                                      <span className="text-green-600">Activate</span>
                                    </>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => setDeleteId(employee.id)}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination */}
          {!loading && totalItems > 0 && (
            <div className="border-t px-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent
          data-testid="employee-form-dialog"
          className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Doe"
                required
                data-testid="employee-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@company.com"
                required
                data-testid="employee-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">
                Password {editingEmployee ? '(leave blank to keep current)' : '*'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder={editingEmployee ? '••••••••' : 'Min 6 characters'}
                required={!editingEmployee}
                data-testid="employee-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 (555) 000-0000"
                data-testid="employee-phone-input"
              />
              <button
                type="button"
                onClick={() => setShowSmsDisclosure(prev => !prev)}
                className="text-xs text-primary hover:underline mt-1"
                data-testid="toggle-sms-disclosure"
              >
                {showSmsDisclosure ? 'Hide' : 'Show'} SMS terms
              </button>
              {showSmsDisclosure && (
                <div
                  className="text-xs text-muted-foreground bg-muted/40 border rounded-md p-2 leading-relaxed"
                  data-testid="sms-disclosure-text"
                >
                  By entering your number, you agree to receive mobile messages.
                  Message frequency may vary. Text HELP for help. Text STOP to
                  cancel. Message and data rates may apply. See our{' '}
                  <a
                    href="/sms-terms"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    SMS Terms
                  </a>{' '}
                  and{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    Privacy Policy
                  </a>
                  .
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Roles *</Label>
              <div className="border rounded-md p-3 space-y-2">
                <p className="text-xs text-muted-foreground mb-2">Select one or more roles for this employee</p>
                <div className="flex flex-wrap gap-2">
                  {availableRoles.map((role) => {
                    const isSelected = formData.roles?.includes(role.value);
                    return (
                      <button
                        key={role.value}
                        type="button"
                        onClick={() => handleRoleToggle(role.value)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                        }`}
                        data-testid={`role-toggle-${role.value}`}
                      >
                        {role.label}
                      </button>
                    );
                  })}
                </div>
                {formData.roles?.length > 1 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Primary role: <span className="font-medium capitalize">{formData.roles[0]}</span>
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="office">Office Location</Label>
              <Select
                value={formData.office_location_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, office_location_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an office" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Office</SelectItem>
                  {offices.map((office) => (
                    <SelectItem key={office.id} value={office.id}>
                      {office.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Divisions <span className="text-destructive">*</span></Label>
              <div className="border rounded-md p-3 space-y-2">
                {/* Display existing cost centers */}
                {formData.cost_centers && formData.cost_centers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.cost_centers.map((cc) => (
                      <Badge key={cc} variant="secondary" className="flex items-center gap-1 pr-1">
                        {cc}
                        <button
                          type="button"
                          onClick={() => handleRemoveCostCenter(cc)}
                          className="ml-1 hover:bg-muted rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                {/* Add new cost center input */}
                <div className="flex gap-2">
                  <Input
                    id="new_cost_center"
                    placeholder="Enter division name"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCostCenter(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    data-testid="employee-cost-center-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      const input = document.getElementById('new_cost_center');
                      if (input) {
                        handleAddCostCenter(input.value);
                        input.value = '';
                      }
                    }}
                  >
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Type a division name and press Enter or click + to add
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Certifications</Label>
              <div className="border rounded-md p-4 space-y-3 max-h-40 overflow-y-auto">
                {certifications.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No certifications available.</p>
                ) : (
                  certifications.map((cert) => (
                    <div key={cert.id} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`cert-${cert.id}`} 
                        checked={(formData.certification_ids || []).includes(cert.id)}
                        onCheckedChange={() => handleCertificationToggle(cert.id)}
                      />
                      <Label htmlFor={`cert-${cert.id}`} className="text-sm font-normal cursor-pointer">
                        {cert.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-employee-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : editingEmployee ? (
                  'Update Employee'
                ) : (
                  'Add Employee'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Employee</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this employee? This will remove their account and all associated data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete Employee
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Employees from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV file with the following columns: Name, Email, Password, Phone, Role, Cost Center
            </p>
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <span className="text-primary hover:underline">Click to upload</span>
                <span className="text-muted-foreground"> or drag and drop</span>
              </label>
              <p className="text-xs text-muted-foreground mt-1">CSV files only</p>
            </div>
            {importLoading && (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Importing...</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Delete {selectedIds.length} Employee{selectedIds.length > 1 ? 's' : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.length} employee{selectedIds.length > 1 ? 's' : ''}? 
              This will permanently remove their accounts and all associated data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkActionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              className="bg-destructive text-destructive-foreground"
              disabled={bulkActionLoading}
            >
              {bulkActionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                `Delete ${selectedIds.length} Employee${selectedIds.length > 1 ? 's' : ''}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Activate/Deactivate Confirmation */}
      <AlertDialog open={!!showBulkStatusDialog} onOpenChange={() => setShowBulkStatusDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {showBulkStatusDialog === 'activate' ? (
                <>
                  <UserCheck className="w-5 h-5 text-green-500" />
                  Activate {selectedIds.length} Employee{selectedIds.length > 1 ? 's' : ''}
                </>
              ) : (
                <>
                  <UserX className="w-5 h-5 text-orange-500" />
                  Deactivate {selectedIds.length} Employee{selectedIds.length > 1 ? 's' : ''}
                </>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {showBulkStatusDialog === 'activate' 
                ? `Are you sure you want to activate ${selectedIds.length} employee${selectedIds.length > 1 ? 's' : ''}? They will be able to log in and be assigned to jobs.`
                : `Are you sure you want to deactivate ${selectedIds.length} employee${selectedIds.length > 1 ? 's' : ''}? They will not be able to log in or be assigned to new jobs.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkActionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleBulkStatusChange(showBulkStatusDialog === 'activate')} 
              className={showBulkStatusDialog === 'activate' 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-orange-600 hover:bg-orange-700'
              }
              disabled={bulkActionLoading}
            >
              {bulkActionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : showBulkStatusDialog === 'activate' ? (
                `Activate ${selectedIds.length} Employee${selectedIds.length > 1 ? 's' : ''}`
              ) : (
                `Deactivate ${selectedIds.length} Employee${selectedIds.length > 1 ? 's' : ''}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
